import * as Sentry from "@sentry/react";
import { FC, StrictMode, useContext, useEffect } from 'react';
import { createRoot } from 'react-dom/client';

import App from '@/App';
import { AppContext, AuthenticatedUserContext, FormsFeatureContentProvider, FormsFeatureProvider, FormsMetadataContext } from '@ngt/forms';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from "react-router-dom/dist";
import AppSettings from './AppSettings';
import TrialsProvider from "./components/providers/data/TrialsProvider";
import { createSentryAnonymousUser } from "./error";
import formsFeature from "./formsFeature";


const Fallback: FC = () => {    

    const { loading: formsMetadataLoading, error: formsMetadataError } = useContext(FormsMetadataContext);
    const { data: authenticatedUser, loading: authenticatedUserLoading, error: authenticatedUserError } = useContext(AuthenticatedUserContext);
    const { loading: appLoading, error: appError} = useContext(AppContext);

    if (!authenticatedUserLoading &&
        (!!authenticatedUserError || !authenticatedUser)) {
        window.location.href = `/auth/openiddict?redirect=${encodeURIComponent(window.location.pathname + window.location.search?.replace(/&/, '%26'))}`

        return (
            <>
                Loading Auth...
            </>
        )
    }

    if (!!formsMetadataError || !!authenticatedUserError || !!appError) {

        return (
            <>
                Error...
            </>
        );
    }

    if (formsMetadataLoading || authenticatedUserLoading || appLoading) {
        return (
            <>
                Loading...
            </>
        );
    }

    return (
        <>
            Unknown...
        </>
    )
}

if (AppSettings.sentry.dsn) {
    const integrations = [
        // React Router V6 integration.
        // Helps with tracing URL changes and which URLs belong to the 
        // same route, for example:
        //    - https://test.com/form/1
        //    - https://test.com/form/2
        // both belong to the same route */form/*
        Sentry.reactRouterV6BrowserTracingIntegration({
            useEffect: useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
        }),
        // Browser Tracing integration
        // Helps with tracing URLs and other common browser "spans"
        Sentry.browserTracingIntegration(),
        // Replay integration
        // Records user actions and sends to Sentry.
        Sentry.replayIntegration({
            // Whether to mask all text on the page when recording user actions
            // When false only inputs are masked.
            // There is a separate setting to force unmasked inputs.
            maskAllText: false,
            // Whether to mask all images on the page when recording user 
            // actions.
            blockAllMedia: false,
            // Which URLs can the replay run on.
            // URL must contain one of the strings in the array or match one of
            // the regexes supplied
            networkDetailAllowUrls: [window.location.origin],
            // Capture the body of network requests
            // Sentry will still run data scrubbing on the bodies.
            networkCaptureBodies: true
        })
    ];
    if (AppSettings.sentry.enableReportABug) {
        integrations.push(Sentry.feedbackIntegration({
            // Disable the injection of the default widget
            autoInject: false,
            colorScheme: 'system',
            showBranding: false,
            showName: false,
            showEmail: false,
            tags: {
                Application: AppSettings.sentry.application,
                Customer: AppSettings.sentry.customer
            },
            useSentryUser: {
                name: 'fullName',
                email: 'email',
            },
        }));
    }
    Sentry.init({
        // The DSN to use when sending information to Sentry.
        // Identifies a specific project in Sentry.
        dsn: AppSettings.sentry.dsn,
        // Whether to send identifying information to Sentry such as
        // username, useragent, ip, etc
        sendDefaultPii: AppSettings.sentry.sendDefaultPii,
        // Whether to sent available performance metrics to Sentry.
        // These are the basic performance metrics and not the endepth
        // profiling metrics that take a lot more effort to setup.
        enableMetrics: AppSettings.sentry.enableMetrics,
        // Whether to attach stacktraces to actions sent to Sentry.
        attachStacktrace: AppSettings.sentry.attachStacktrace,
        // Whether @sentry/react is in debug mode, logging more information
        // to the console.
        debug: AppSettings.sentry.debug,
        // initialise integrations.
        integrations: integrations,
        // The environment to associate all action sent to Sentry with.
        environment: AppSettings.sentry.environment,
        // Release to associate all actions sent to Sentry with.
        release: AppSettings.sentry.release,
        // Percentage of captured traces that are sent to Sentry.
        // 1.0 = 100%, 0 = 0%
        tracesSampleRate: AppSettings.sentry.tracesSampleRate,
        // Which URLs to trace / record in a trace.
        // Only trace internal URLs don't want to trace every SEO url
        // under the sun.
        tracePropagationTargets: [
            'localhost', 
            /^\//,
            new RegExp(`/^${window.location.origin}`)
        ],
        // Percentage of captured session replays that are sent to Sentry.
        // 1.0 = 100%, 0 = 0%
        replaysSessionSampleRate: AppSettings.sentry.replaysSessionSampleRate,
        // Percentage of captured session replays that are sent to Sentry.
        // when an error occurs
        // 1.0 = 100%, 0 = 0%
        replaysOnErrorSampleRate: AppSettings.sentry.replaysOnErrorSampleRate,
        // Enable logs to be sent to Sentry.
        enableLogs: AppSettings.sentry.enableLogs,
    });
    Sentry.setTag('Application', AppSettings.sentry.application);
    Sentry.setTag('Customer', AppSettings.sentry.customer);
    // Set user to anonymous at start up, this should be overriden by
    // React code if user information is available.
    Sentry.setUser(createSentryAnonymousUser());
}


document.addEventListener('DOMContentLoaded', function(event) {  
    const root = AppSettings.sentry.dsn ? 
        createRoot(
            document.getElementById('root')!,
            {
                // Callback called when React automatically recovers from errors.
                onRecoverableError: Sentry.reactErrorHandler(),
            }
        ) :
        createRoot(document.getElementById('root')!);

    root.render(
        <StrictMode>
            <FormsFeatureProvider value={formsFeature}>
                <FormsFeatureContentProvider fallback={Fallback}>
                    <TrialsProvider>
                        <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
                            <App />
                        </Sentry.ErrorBoundary>
                    </TrialsProvider>
                </FormsFeatureContentProvider>
            </FormsFeatureProvider>
        </StrictMode>
    )
});